import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import Icon from '../../../components/AppIcon';

const ProviderPerformanceBenchmarking = ({ dateRange, provider }) => {
  const providerPerformance = [
    {
      provider: 'Dr. Michael Chen',
      successRate: 97.2,
      patientSatisfaction: 4.9,
      complications: 1.8,
      efficiency: 94,
      qualityScore: 96,
      rank: 1
    },
    {
      provider: 'Dr. Sarah Rodriguez',
      successRate: 96.5,
      patientSatisfaction: 4.9,
      complications: 2.1,
      efficiency: 92,
      qualityScore: 95,
      rank: 2
    },
    {
      provider: 'Dr. James Thompson',
      successRate: 95.8,
      patientSatisfaction: 4.8,
      complications: 2.5,
      efficiency: 90,
      qualityScore: 93,
      rank: 3
    }
  ];

  const qualityIndicators = [
    {
      indicator: 'Clinical Excellence',
      drChen: 96,
      drRodriguez: 94,
      drThompson: 92,
      benchmark: 90
    },
    {
      indicator: 'Patient Safety',
      drChen: 98,
      drRodriguez: 97,
      drThompson: 95,
      benchmark: 93
    },
    {
      indicator: 'Treatment Efficiency',
      drChen: 94,
      drRodriguez: 92,
      drThompson: 90,
      benchmark: 88
    },
    {
      indicator: 'Patient Satisfaction',
      drChen: 98,
      drRodriguez: 98,
      drThompson: 96,
      benchmark: 92
    },
    {
      indicator: 'Outcome Quality',
      drChen: 97,
      drRodriguez: 96,
      drThompson: 95,
      benchmark: 91
    }
  ];

  const radarData = [
    { metric: 'Success Rate', drChen: 97, drRodriguez: 96, drThompson: 95, benchmark: 90 },
    { metric: 'Satisfaction', drChen: 98, drRodriguez: 98, drThompson: 96, benchmark: 92 },
    { metric: 'Safety', drChen: 98, drRodriguez: 97, drThompson: 95, benchmark: 93 },
    { metric: 'Efficiency', drChen: 94, drRodriguez: 92, drThompson: 90, benchmark: 88 },
    { metric: 'Quality', drChen: 97, drRodriguez: 96, drThompson: 95, benchmark: 91 }
  ];

  const performanceMetrics = [
    {
      label: 'Top Performer',
      value: 'Dr. Chen',
      icon: 'Award',
      color: 'bg-success/10 text-success',
      subtitle: 'Overall quality score: 96'
    },
    {
      label: 'Avg Quality Score',
      value: '94.7',
      icon: 'BarChart3',
      color: 'bg-primary/10 text-primary',
      subtitle: 'All providers'
    },
    {
      label: 'Benchmark Met',
      value: '100%',
      icon: 'Target',
      color: 'bg-warning/10 text-warning',
      subtitle: 'Quality standards'
    },
    {
      label: 'Peer Comparison',
      value: '+8.2%',
      icon: 'Users',
      color: 'bg-muted text-muted-foreground',
      subtitle: 'Above national avg'
    }
  ];

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
          <Icon name="Users" size={20} className="text-primary" />
          Provider Performance Benchmarking
        </h2>
        <span className="text-xs text-muted-foreground">
          {dateRange === 'day' ? 'Today' : dateRange === 'week' ? 'This Week' : dateRange === 'month' ? 'This Month' : dateRange === 'quarter' ? 'This Quarter' : 'This Year'}
        </span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {performanceMetrics?.map((metric, index) => (
          <div key={index} className="bg-muted/50 rounded-lg p-3 border border-border">
            <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${metric?.color} mb-2`}>
              <Icon name={metric?.icon} size={16} />
            </div>
            <p className="text-2xl font-bold text-foreground mb-1">{metric?.value}</p>
            <p className="text-xs text-muted-foreground">{metric?.label}</p>
            <p className="text-xs text-muted-foreground">{metric?.subtitle}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-4">Quality Indicators Comparison</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={qualityIndicators} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  dataKey="indicator"
                  stroke="var(--color-muted-foreground)"
                  style={{ fontSize: '10px' }}
                  angle={-15}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  stroke="var(--color-muted-foreground)"
                  style={{ fontSize: '11px' }}
                  domain={[0, 100]}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    color: 'var(--color-foreground)'
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Bar dataKey="drChen" fill="#2563EB" name="Dr. Chen" />
                <Bar dataKey="drRodriguez" fill="#059669" name="Dr. Rodriguez" />
                <Bar dataKey="drThompson" fill="#D97706" name="Dr. Thompson" />
                <Bar dataKey="benchmark" fill="#6B7280" name="Benchmark" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-foreground mb-4">Performance Radar Analysis</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid stroke="var(--color-border)" />
                <PolarAngleAxis
                  dataKey="metric"
                  stroke="var(--color-muted-foreground)"
                  style={{ fontSize: '11px' }}
                />
                <PolarRadiusAxis
                  angle={90}
                  domain={[0, 100]}
                  stroke="var(--color-muted-foreground)"
                  style={{ fontSize: '10px' }}
                />
                <Radar
                  name="Dr. Chen"
                  dataKey="drChen"
                  stroke="#2563EB"
                  fill="#2563EB"
                  fillOpacity={0.3}
                />
                <Radar
                  name="Dr. Rodriguez"
                  dataKey="drRodriguez"
                  stroke="#059669"
                  fill="#059669"
                  fillOpacity={0.3}
                />
                <Radar
                  name="Dr. Thompson"
                  dataKey="drThompson"
                  stroke="#D97706"
                  fill="#D97706"
                  fillOpacity={0.3}
                />
                <Radar
                  name="Benchmark"
                  dataKey="benchmark"
                  stroke="#6B7280"
                  fill="#6B7280"
                  fillOpacity={0.1}
                />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    color: 'var(--color-foreground)'
                  }}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-foreground mb-4">Provider Rankings & Detailed Metrics</h3>
        <div className="space-y-3">
          {providerPerformance?.map((prov, index) => (
            <div key={index} className="bg-muted/50 rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground font-bold text-sm">
                    #{prov?.rank}
                  </div>
                  <span className="text-sm font-semibold text-foreground">{prov?.provider}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="Award" size={16} className="text-warning" />
                  <span className="text-sm font-bold text-foreground">Quality Score: {prov?.qualityScore}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-1">Success Rate</p>
                  <p className="text-sm font-semibold text-success">{prov?.successRate}%</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-1">Satisfaction</p>
                  <p className="text-sm font-semibold text-warning">{prov?.patientSatisfaction}/5.0</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-1">Complications</p>
                  <p className="text-sm font-semibold text-error">{prov?.complications}%</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-1">Efficiency</p>
                  <p className="text-sm font-semibold text-primary">{prov?.efficiency}%</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-1">Overall</p>
                  <p className="text-sm font-semibold text-foreground">{prov?.qualityScore}/100</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProviderPerformanceBenchmarking;